/**
 * Shared block-insertion commands used by both the SlashCommandPlugin ("/" menu)
 * and the DraggableBlockPlugin ("+" picker), so the two stay in sync.
 *
 * Each `run` operates on the CURRENT selection (converts/inserts at the cursor).
 * The slash menu runs it on the block where "/" was typed; the "+" picker first
 * inserts an empty paragraph after the hovered block and selects it, then runs.
 */
import { $createParagraphNode, $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $setBlocksType } from '@lexical/selection';
import { ElementNode } from 'lexical';

export interface BlockCommand {
  key: string;
  label: string;
  icon: string;
  description: string;
  keywords: string[];
  run: (editor: LexicalEditor) => void;
}

const setBlock = (editor: LexicalEditor, factory: () => ElementNode) => {
  editor.update(() => {
    const sel = $getSelection();
    if ($isRangeSelection(sel)) $setBlocksType(sel, factory);
  });
};

export const BLOCK_COMMANDS: BlockCommand[] = [
  {
    key: 'paragraph',
    label: 'Paragraph',
    icon: '¶',
    description: 'Normal text block',
    keywords: ['paragraph', 'text', 'normal', 'p'],
    run: (editor) => setBlock(editor, () => $createParagraphNode()),
  },
  {
    key: 'h1',
    label: 'Heading 1',
    icon: 'H1',
    description: 'Large section heading',
    keywords: ['h1', 'heading1', 'heading 1', 'title'],
    run: (editor) => setBlock(editor, () => $createHeadingNode('h1')),
  },
  {
    key: 'h2',
    label: 'Heading 2',
    icon: 'H2',
    description: 'Medium section heading',
    keywords: ['h2', 'heading2', 'heading 2'],
    run: (editor) => setBlock(editor, () => $createHeadingNode('h2')),
  },
  {
    key: 'h3',
    label: 'Heading 3',
    icon: 'H3',
    description: 'Small section heading',
    keywords: ['h3', 'heading3', 'heading 3'],
    run: (editor) => setBlock(editor, () => $createHeadingNode('h3')),
  },
  {
    key: 'bullet',
    label: 'Bullet List',
    icon: '•',
    description: 'Unordered list',
    keywords: ['ul', 'unordered', 'bullet', 'list', 'bulleted'],
    run: (editor) => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
  },
  {
    key: 'number',
    label: 'Numbered List',
    icon: '1.',
    description: 'Ordered numbered list',
    keywords: ['ol', 'ordered', 'numbered', 'list', 'number'],
    run: (editor) => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
  },
  {
    key: 'check',
    label: 'Check List',
    icon: '☑',
    description: 'Task list with checkboxes',
    keywords: ['check', 'todo', 'checklist', 'checkbox', 'task'],
    run: (editor) => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
  },
  {
    key: 'quote',
    label: 'Quote',
    icon: '"',
    description: 'Indented blockquote',
    keywords: ['quote', 'blockquote', 'q'],
    run: (editor) => setBlock(editor, () => $createQuoteNode()),
  },
  {
    key: 'code',
    label: 'Code Block',
    icon: '</>',
    description: 'Multi-line code block',
    keywords: ['code', 'codeblock', 'pre', 'snippet'],
    run: (editor) => setBlock(editor, () => $createCodeNode()),
  },
  {
    key: 'divider',
    label: 'Divider',
    icon: '—',
    description: 'Horizontal divider line',
    keywords: ['divider', 'hr', 'rule', 'horizontal', 'separator'],
    run: (editor) => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
  },
  {
    key: 'table',
    label: 'Table',
    icon: '▦',
    description: '3×3 table with a header row',
    keywords: ['table', 'grid', 'rows', 'columns', 'cells'],
    run: (editor) =>
      editor.dispatchCommand(INSERT_TABLE_COMMAND, {
        columns: '3',
        rows: '3',
        includeHeaders: { rows: true, columns: false },
      }),
  },
];
